import { LockOutlined, UserOutlined } from '@ant-design/icons';
import type { LoginInput } from '@financiar/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Card, Form, Input, Typography } from 'antd';
import { useNavigate } from 'react-router';
import type { ApiError } from '../../api/client';
import { getApiError } from '../../api/client';
import { login } from './api';

const { Title, Text } = Typography;

/**
 * The one screen in this task with a bespoke look (controller ruling R30):
 * warm paper background, centered card, "Financiar" wordmark (`.brand`,
 * Roboto 700), single full-width primary button, error surfaced in an in-card
 * Alert (never a toast — the user's eyes are already on the card).
 */
export function LoginPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<LoginInput>();

  const mutation = useMutation<{ username: string }, ApiError, LoginInput>({
    mutationFn: login,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
      navigate('/', { replace: true });
    },
  });

  const apiError = getApiError(mutation.error);

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f6f5f1',
        padding: 16,
      }}
    >
      <Card style={{ width: '100%', maxWidth: 380 }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <Title level={2} className="brand" style={{ margin: 0 }}>
            Financiar
          </Title>
          <Text type="secondary">Facturare internă</Text>
        </div>

        {apiError && (
          <Alert type="error" message={apiError.message} showIcon style={{ marginBottom: 16 }} />
        )}

        <Form<LoginInput>
          form={form}
          layout="vertical"
          disabled={mutation.isPending}
          onFinish={(values) => mutation.mutate(values)}
        >
          <Form.Item
            name="username"
            label="Utilizator"
            rules={[{ required: true, message: 'Introdu numele de utilizator.' }]}
          >
            <Input prefix={<UserOutlined />} autoFocus autoComplete="username" />
          </Form.Item>
          <Form.Item
            name="password"
            label="Parolă"
            rules={[{ required: true, message: 'Introdu parola.' }]}
          >
            <Input.Password prefix={<LockOutlined />} autoComplete="current-password" />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Button type="primary" htmlType="submit" block loading={mutation.isPending}>
              Autentificare
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
}
